/**
 * Shrink a photograph in the browser before it is uploaded.
 *
 * A modern phone camera produces 4–8 MB per frame; six of those is 40 MB over a
 * mobile connection for an image the model will look at 1568 px wide anyway.
 * Downscaling first is the single largest thing the client does for latency.
 *
 * `createImageBitmap` + `OffscreenCanvas` keeps the work off the main thread
 * where it is available. Safari only grew `OffscreenCanvas` recently, so there
 * is a `<canvas>` fallback and it is not dead code.
 */

export const ANALYSIS_EDGE = 1568
export const ORIGINAL_EDGE = 2400
export const MENU_EDGE = 2600

export interface Downscaled {
  analysis: Blob
  original: Blob
  seenAt: string | null
}

function fit(width: number, height: number, edge: number) {
  const scale = Math.min(1, edge / Math.max(width, height))
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

async function render(bitmap: ImageBitmap, edge: number, quality: number): Promise<Blob> {
  const { width, height } = fit(bitmap.width, bitmap.height, edge)

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height)
    const context = canvas.getContext('2d')
    if (!context) throw new Error('No 2D context.')
    context.drawImage(bitmap, 0, 0, width, height)
    return canvas.convertToBlob({ type: 'image/jpeg', quality })
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height)
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image.'))),
      'image/jpeg',
      quality,
    ),
  )
}

/**
 * One blob, at more resolution than a shelf photograph gets.
 *
 * A menu is read rather than looked at. A shelf photograph only has to carry a
 * label big enough to recognise, but a wine list is eight-point type in three
 * columns, and at `ANALYSIS_EDGE` the producer and the vintage are the first
 * things to turn to mush — which is a transcription that quietly loses lines
 * rather than one that fails.
 *
 * There is no second variant because nobody ever looks back at a photograph of
 * a wine list. It is read once and the transcription is what is kept.
 */
export async function downscaleMenu(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  try {
    return await render(bitmap, MENU_EDGE, 0.85)
  } finally {
    bitmap.close()
  }
}

export async function downscale(file: File): Promise<Downscaled> {
  const bitmap = await createImageBitmap(file)
  try {
    return {
      // Two variants: what the model reads, and what the timeline shows.
      analysis: await render(bitmap, ANALYSIS_EDGE, 0.8),
      original: await render(bitmap, ORIGINAL_EDGE, 0.85),
      // `lastModified` is when the photograph was taken for a camera capture,
      // which is closer to the truth than the upload time. Reading real EXIF
      // would mean parsing the file; this is within a second in practice.
      seenAt: file.lastModified ? new Date(file.lastModified).toISOString() : null,
    }
  } finally {
    bitmap.close()
  }
}
